import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactSVG from 'react-svg';
import { Paper } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useContactGroups, useContacts } from '~/hooks';
import { RootState } from '~/store/config';
import { UserIcon, Teams } from '~/assets/svgIcons';
import { log } from '~/helpers';
import {
  ContactOrGroupList,
  ContactOrGroup,
  Contact,
  ContactGroup,
  ContactList,
  ContactGroupList,
  SmsTemplate,
  SmsSender,
} from '~/types/accountInfo';
import {
  filterOptions,
  filterSelectedContacts,
  filterSelectedGroups,
  createRecipientContact,
  getInvalidParticipants,
  normalizeE164OrReturnOriginal,
  parsePhonebookNumberStandard,
  formatContactNameOrNumberLabel,
  isContact,
  isContactGroup,
} from './utils';
import {
  Container,
  Chip,
  GroupItems,
  GroupHeader,
  Autocomplete,
  TextField,
} from './styles';

export default function RecipientsSelector({
  initialValues,
  onChange,
  onValidChange,
  maxRecipients,
  template,
  sender,
}: {
  /**
   * The initial selected participants list.
   * Comes from navigation state or parent component state.
   */
  initialValues: ContactList;
  onChange(value: any): void;
  onValidChange(value: any): void;
  /**
   * The maximum number of recipients that can be selected.
   */
  maxRecipients?: number;
  template: SmsTemplate | null;
  sender: SmsSender | null;
}) {
  // REF's
  const isFirstRender = useRef(true);

  // SELECTOR's
  const { userCountry } = useSelector((state: RootState) => state.auth);

  // HOOK's
  const { t } = useTranslation();
  const { groups } = useContactGroups();
  const { contacts } = useContacts();

  // STATE's
  const [values, setValues] = useState<ContactOrGroupList>([]);
  const [invalidParticipants, setInvalidParticipants] = useState<any>([]);

  // CONST's
  const options = [
    ...filterSelectedContacts(contacts, values as ContactList),
    ...filterSelectedGroups(groups, values as ContactGroupList),
  ];

  useEffect(() => {
    /**
     *On the first render, set the initial recipient values, which may come from navigation (for example, from the contacts list).
     */
    if (isFirstRender.current) {
      if (initialValues.length) {
        setValues(initialValues);
        // @ts-ignore
        onChange([createRecipientContact(...initialValues)]);
      }
      // Set it to false after first render
      isFirstRender.current = false;
    }
  }, [setValues, initialValues, updateParticipantsList, onChange]);

  useEffect(() => {
    onValidChange(getInvalidParticipants(values, template, sender));
    setInvalidParticipants(getInvalidParticipants(values, template, sender));
  }, [values, template, sender, onValidChange, setInvalidParticipants]);

  // FIXME: Move to useCallBack once we get a chance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function updateParticipantsList(_: any, participants: any) {
    try {
      if (maxRecipients && participants.length > maxRecipients) {
        return;
      }

      let participantsList: ContactOrGroupList = [];

      // If the last item is a string, it means the user typed a number that is not in contacts or groups
      if (typeof participants[participants.length - 1] === 'string') {
        const inputNumber = participants[participants.length - 1];
        // User manually insert participant, not of contacts or groups
        const unlistedParticipant = createRecipientContact({
          mobile: parsePhonebookNumberStandard(
            normalizeE164OrReturnOriginal(inputNumber, userCountry)
          ),
        });

        participantsList = [...values, unlistedParticipant];

        setValues(participantsList);
      } else {
        participantsList = participants;
        setValues(participants);
      }

      onChange(participantsList);
    } catch (err) {
      log.error('Error updating participants list.', err);
    }
  }

  /**
   * Checks if the participant is in the invalidParticipants list
   * to highlight it in the UI Clip as "Red"
   */
  const isInvalidRecipient = (recipient: ContactOrGroup): boolean => {
    return invalidParticipants.some(
      (p: ContactOrGroup) =>
        (isContactGroup(p) &&
          p.groupId === (recipient as ContactGroup).groupId) ||
        (isContact(p) && p.id === (recipient as Contact).id)
    );
  };

  const renderGroup = (params: any) => {
    const getGroupIcon = (groupName: string) => {
      // FIXME: Wrap ReactSVG and move styles "icon-groups" inside styled component
      switch (groupName) {
        case 'contacts':
          return <ReactSVG className="icon-groups" src={UserIcon} />;
        case 'contactGroups':
          return <ReactSVG className="icon-groups" src={Teams} />;
        default:
          return <ReactSVG className="icon-groups" src={UserIcon} />;
      }
    };

    const title =
      params.group === 'contacts'
        ? t('screens.conversation.recipientsSelector.groupByTitleContact')
        : t(
            'screens.conversation.recipientsSelector.groupByTitleContactGroups'
          );

    return (
      <li key={params.key}>
        <GroupHeader>
          {getGroupIcon(params.group)}
          {title}
        </GroupHeader>
        <GroupItems>{params.children}</GroupItems>
      </li>
    );
  };

  const renderTags = (tagsList: ContactOrGroupList, getTagProps: any) => {
    return tagsList.map((tagItem: ContactOrGroup, index: number) => {
      return (
        <Chip
          title={
            isContact(tagItem)
              ? tagItem?.mobile || tagItem?.landline
              : tagItem?.groupName
          }
          deleteIcon={<Close className="icon-close" />}
          isInvalid={isInvalidRecipient(tagItem)}
          avatar={
            <ReactSVG
              className="icon-chip"
              src={isContact(tagItem) ? UserIcon : Teams}
            />
          }
          label={
            isContact(tagItem)
              ? formatContactNameOrNumberLabel(tagItem)
              : tagItem?.groupName
          }
          {...getTagProps({ index })}
        />
      );
    });
  };

  return (
    <Container>
      <Autocomplete
        data-test-id="recipient-selector"
        freeSolo
        multiple
        autoSelect
        filterSelectedOptions
        disablePortal
        forcePopupIcon
        value={values}
        options={options}
        isInvalid={!!invalidParticipants.length}
        groupBy={(option: ContactOrGroup) =>
          isContact(option) ? 'contacts' : 'contactGroups'
        }
        getOptionLabel={(option: ContactOrGroup) => option.name}
        renderGroup={renderGroup}
        filterOptions={filterOptions}
        renderInput={(params: any) => (
          <TextField
            {...params}
            variant="standard"
            placeholder={
              values?.length === 0
                ? t('screens.conversation.recipientsSelector.chooseRecipients')
                : undefined
            }
          />
        )}
        renderTags={renderTags}
        PaperComponent={({ children }: any) => (
          <Paper style={{ width: 380, marginTop: 10, marginLeft: -5 }}>
            {children}
          </Paper>
        )}
        onChange={updateParticipantsList}
      />
    </Container>
  );
}

RecipientsSelector.defaultProps = {
  maxRecipients: 0,
};
